// CLIENT PORTAL — an UNAMBIGUOUS currency symbol (§A, 2026-09-03).
//
// ⚠️⚠️ `Intl` CANNOT DO THIS FROM ONE LOCALE, and that is why there is a map
// here rather than a formatter call. Measured against Node's own ICU:
//
//   locale   currency   narrowSymbol   symbol
//   en-AU    AUD        $              $
//   en-AU    NZD        $              NZD
//   en-AU    USD        $              USD
//   en-US    AUD        $              A$
//   en-NZ    NZD        $              $
//
// So `narrowSymbol` renders AUD, NZD, USD and SGD all as a bare `$` in every
// locale, and `symbol` disambiguates only the currencies that are FOREIGN to the
// chosen locale — it always renders the local one as `$`. There is no locale
// from which one call distinguishes every currency this roster uses.
//
// ⚠️ WHY IT MATTERS HERE AND NOT EVERYWHERE ELSE (yet). A cost AXIS is a single
// scale with one label, and an axis reading `$` on a report that could be
// Australian or New Zealand is a money figure whose currency the reader has to
// guess. The rest of the report renders money per currency in lists, where the
// same bare `$` is ambiguous in ATTRIBUTION rather than in value — CLAUDE.md
// records that as report-wide and still owed. This is the piece the chart
// depends on; the sweep is still its own change.

/**
 * The currencies this roster actually operates in.
 *
 * ⚠️ EXPLICIT, and short on purpose. A wide table of every ISO currency would be
 * a list nobody has checked; these four are the ones the agency's accounts carry
 * (the same set the internal display-currency selector offers).
 */
const DISAMBIGUATED: Record<string, string> = {
  AUD: 'A$',
  NZD: 'NZ$',
  USD: 'US$',
  SGD: 'S$',
};

/**
 * A symbol that says WHICH dollar, or the ISO code when we cannot be sure.
 *
 * ⚠️⚠️ IT NEVER RETURNS A BARE `$`. That is the whole contract: a currency we do
 * not have an unambiguous symbol for falls through to its ISO CODE, which is
 * ugly and correct, rather than to a symbol that could mean four things. A
 * non-dollar symbol (£, €, ¥) is already unambiguous and is used as-is.
 */
export function disambiguatedCurrencySymbol(currency: string): string {
  const mapped = DISAMBIGUATED[currency.toUpperCase()];
  if (mapped) return mapped;
  const narrow = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  })
    .formatToParts(0)
    .find((p) => p.type === 'currency')?.value;
  // ⚠️ A bare `$` from an unmapped currency is exactly the ambiguity this exists
  // to remove, so it is rejected in favour of the code.
  if (!narrow || narrow === '$') return currency.toUpperCase();
  return narrow;
}
